/**
 * Workshop showcase page — demonstrates all workflow frame scenarios.
 *
 * Renders sequential, parallel, conditional, mid-execution, approval,
 * and failed workflow states, plus the side panel and execution sidebar
 * components side by side.
 */

import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import { WorkflowFramePanel } from "@/workshop/components/shapes/workflow-frame/workflow-frame-panel";
import {
  MOCK_SEQUENTIAL,
  MOCK_PARALLEL,
  MOCK_CONDITIONAL,
  MOCK_MIDRUN,
  MOCK_APPROVAL,
  MOCK_FAILED,
  MOCK_RUNS,
} from "@/workshop/data/mock-workflows";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const noop = () => {};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="mb-10"
      data-testid={`workflow-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap gap-6">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function WorkflowPage() {
  return (
    <div className="p-8" data-testid="workflow-page">
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Workflow Frame</h1>
        <p className="mt-1 text-sm text-white/40">
          Visual flowchart workflow frames with nodes, edges, parallel branches,
          conditionals, and hover popovers.
        </p>
      </div>

      {/* ---- Section 1: Sequential Flow ---- */}
      <Section title="Sequential Flow">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_SEQUENTIAL.name}
            flowSteps={MOCK_SEQUENTIAL.steps}
            status="done"
            agentName="deploy-agent"
          />
        </div>
      </Section>

      {/* ---- Section 2: Parallel Branching ---- */}
      <Section title="Parallel Branching">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_PARALLEL.name}
            flowSteps={MOCK_PARALLEL.steps}
            status="streaming"
            agentName="intel-agent"
            onApprove={noop}
            onReject={noop}
          />
        </div>
      </Section>

      {/* ---- Section 3: Conditional Split ---- */}
      <Section title="Conditional Split">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_CONDITIONAL.name}
            flowSteps={MOCK_CONDITIONAL.steps}
            status="done"
            agentName="review-agent"
          />
        </div>
      </Section>

      {/* ---- Section 4: Mid-Execution ---- */}
      <Section title="Mid-Execution">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_MIDRUN.name}
            flowSteps={MOCK_MIDRUN.steps}
            status="streaming"
            agentName="codegen-agent"
          />
        </div>
      </Section>

      {/* ---- Section 5: Approval Waiting ---- */}
      <Section title="Approval Waiting">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_APPROVAL.name}
            flowSteps={MOCK_APPROVAL.steps}
            status="streaming"
            agentName="report-agent"
            onApprove={noop}
            onReject={noop}
          />
        </div>
      </Section>

      {/* ---- Section 6: Failed Step ---- */}
      <Section title="Failed Step">
        <div className="w-fit">
          <WorkflowFrame
            title={MOCK_FAILED.name}
            flowSteps={MOCK_FAILED.steps}
            status="error"
            agentName="ci-agent"
          />
        </div>
      </Section>

      {/* ---- Section 7: Side Panel ---- */}
      <Section title="Side Panel">
        <div className="flex gap-4">
          <div className="w-fit">
            <WorkflowFrame
              title={MOCK_PARALLEL.name}
              flowSteps={MOCK_PARALLEL.steps}
              status="streaming"
              agentName="intel-agent"
              onApprove={noop}
              onReject={noop}
            />
          </div>
          <div className="min-h-[480px]">
            <WorkflowFramePanel
              name="Weekly Competitor Report"
              triggerType={MOCK_PARALLEL.trigger.type}
              schedule={MOCK_PARALLEL.trigger.schedule}
              steps={MOCK_PARALLEL.steps}
              runs={MOCK_RUNS}
            />
          </div>
        </div>
      </Section>

    </div>
  );
}
